/**
 * All widget CSS, injected into the Shadow DOM so nothing leaks in or out of the
 * host page.
 *
 * **Palette: their brand, our elevation.**
 *
 * The practice's site is built on #080c16 and #0f1729 with a #0d9488 teal accent.
 * This widget originally used those exact values, which made it genuinely native
 * — and completely invisible. A floating assistant that shares its background
 * with the page behind it stops reading as a separate object; the panel edge
 * disappears and it looks like a section of the page that happens to hover.
 *
 * So the accent stays theirs — teal is the brand, and on a medical site a
 * clashing widget reads as an advert. What changes is depth. In a dark UI two
 * surfaces can't be separated by luminance alone, so four things do the work
 * together: a lifted base, a long shadow, a teal rim light, and frosted glass on
 * the header and composer. The base sits 1.36:1 above their section colour and
 * 1.49:1 above their page — enough to own an edge, not enough to shout.
 *
 * Every text pairing here clears 4.5:1.
 *
 * Reference (their CSS, by frequency): #0d9488 ×84, #0f1729 ×26, #f1f5f9 ×20,
 * #080c16 ×14, #2dd4bf ×10.
 */
export const CSS = `
:host {
  /* Surfaces — each step is a measured lift on the one below it. */
  --vm-bg: #22304f;
  --vm-bg-2: #1b2743;
  --vm-bg-deep: #1a2540;
  --vm-surface: #2b3a5e;
  --vm-surface-2: #35456d;
  --vm-raised: #3d4e79;

  --vm-border: rgba(160,196,255,0.13);
  --vm-border-strong: rgba(160,196,255,0.22);
  --vm-rim: rgba(45,212,191,0.30);

  --vm-text: #f1f5f9;
  --vm-muted: rgba(241,245,249,0.68);
  --vm-faint: rgba(241,245,249,0.46);

  /* Their teal. The dark end carries white text; the bright end carries dark. */
  --vm-accent: #0d9488;
  --vm-accent-deep: #0b7c72;
  --vm-accent-bright: #2dd4bf;
  --vm-accent-ink: #5eead4;
  --vm-accent-soft: rgba(45,212,191,0.15);

  --vm-radius: 16px;
  --vm-font: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  --vm-mono: ui-monospace, SFMono-Regular, Menlo, monospace;
  --vm-spring: cubic-bezier(.22,1,.36,1);
  all: initial;
}

* { box-sizing: border-box; }

.root {
  position: fixed;
  right: 20px;
  /* max() rather than an addition: on a desktop or an Android device the inset
   * is 0 and this is plainly 20px, but on an iPhone the launcher would otherwise
   * sit under the home-gesture bar, where a tap swipes the browser away instead
   * of opening the widget. Only non-zero when the host page sets
   * viewport-fit=cover, and harmless when it does not. */
  bottom: max(20px, env(safe-area-inset-bottom));
  z-index: 2147483000;
  font-family: var(--vm-font);
  color: var(--vm-text);
}

/* ------------------------------------------------------------------ launcher */

/*
 * The incumbent's launcher is a flat grey pill that reads as page furniture.
 * Ours is the brightest object in the corner — same teal, far more presence,
 * with a slow shine sweep so it looks alive without demanding to be dismissed.
 */
.launcher {
  position: relative; overflow: hidden;
  display: flex; align-items: center; gap: 11px;
  padding: 15px 25px 15px 20px;
  border: 0; border-radius: 999px;
  background: linear-gradient(135deg, #34e0ca 0%, #14b8a6 45%, #0b8177 100%);
  color: #032422; font: 700 15px/1 var(--vm-font); letter-spacing: -.01em;
  cursor: pointer;
  box-shadow:
    0 14px 38px rgba(0,0,0,.5),
    0 0 30px -8px rgba(45,212,191,.65),
    inset 0 1px 0 rgba(255,255,255,.35);
  transition: transform .28s var(--vm-spring), box-shadow .28s var(--vm-spring);
}
.launcher::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: -70%;
  width: 45%; transform: skewX(-20deg);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.4), transparent);
  animation: sweep 5.5s ease-in-out infinite;
}
@keyframes sweep {
  0%, 62%   { left: -70%; }
  92%, 100% { left: 130%; }
}
.launcher:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow:
    0 20px 46px rgba(0,0,0,.55),
    0 0 40px -4px rgba(45,212,191,.85),
    inset 0 1px 0 rgba(255,255,255,.45);
}
.launcher:active { transform: translateY(-1px) scale(.99); }
.launcher:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
.launcher svg { width: 20px; height: 20px; }

/* Signals the assistant is live without an intrusive auto-open. */
.launcher-dot {
  position: absolute; top: 11px; right: 13px;
  width: 7px; height: 7px; border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 0 rgba(255,255,255,.85);
  animation: pulse 2.6s ease-out infinite;
}
@keyframes pulse {
  0%   { box-shadow: 0 0 0 0 rgba(255,255,255,.85); }
  70%  { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
}

/* --------------------------------------------------------------------- panel */

.panel {
  position: relative;
  display: flex; flex-direction: column;
  width: min(420px, calc(100vw - 32px));
  height: min(690px, calc(100dvh - 40px));
  /* A vertical base plus two soft light sources, so the panel has interior depth
     instead of reading as one flat rectangle of navy. */
  background:
    radial-gradient(120% 62% at 88% -8%, rgba(45,212,191,.16), transparent 62%),
    radial-gradient(90% 48% at 4% 104%, rgba(56,132,255,.12), transparent 68%),
    linear-gradient(175deg, var(--vm-bg) 0%, var(--vm-bg-2) 100%);
  border: 1px solid var(--vm-rim);
  border-radius: 22px;
  overflow: hidden;
  box-shadow:
    0 36px 100px rgba(0,0,0,.72),
    0 0 0 1px rgba(0,0,0,.55),
    0 0 56px -12px rgba(45,212,191,.24),
    inset 0 1px 0 rgba(255,255,255,.09);
  animation: rise .38s var(--vm-spring);
  transform-origin: bottom right;
}
@keyframes rise {
  from { opacity: 0; transform: translateY(20px) scale(.95); }
  to   { opacity: 1; transform: none; }
}

.header {
  position: relative; z-index: 2;
  display: flex; align-items: center; gap: 12px;
  padding: 15px 16px;
  background: linear-gradient(180deg, rgba(52,70,112,.85), rgba(26,37,64,.72));
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid var(--vm-border);
}
/* One unmistakable "this is the assistant" signal, drawn from their accent. */
.header::before {
  content: ""; position: absolute; inset: 0 0 auto 0; height: 2px;
  background: linear-gradient(90deg, transparent, var(--vm-accent-bright) 35%, #7dd3fc 65%, transparent);
  opacity: .9;
}

.avatar {
  position: relative;
  width: 40px; height: 40px; flex: none;
  display: grid; place-items: center;
  border-radius: 13px;
  background: linear-gradient(140deg, rgba(45,212,191,.3), rgba(13,148,136,.14));
  border: 1px solid rgba(45,212,191,.42);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.16), 0 4px 12px rgba(0,0,0,.3);
  color: #a7f3e4;
  font-weight: 700; font-size: 13.5px; letter-spacing: .04em;
}
.status {
  position: absolute; right: -3px; bottom: -3px;
  width: 12px; height: 12px; border-radius: 50%;
  background: #4ade80; border: 2px solid #1f2c4a;
  box-shadow: 0 0 8px rgba(74,222,128,.8);
}
.title { flex: 1; min-width: 0; }
.title b { display: block; font-size: 14.5px; font-weight: 650; letter-spacing: -.015em; }
.title span { display: block; font-size: 11.5px; color: var(--vm-accent-ink); margin-top: 3px; }
.iconbtn {
  border: 0; background: rgba(255,255,255,.05); color: var(--vm-muted);
  cursor: pointer; padding: 8px; border-radius: 10px; line-height: 0;
  transition: background .18s ease, color .18s ease, transform .18s ease;
}
.iconbtn:hover { background: rgba(255,255,255,.12); color: var(--vm-text); transform: rotate(90deg); }
.iconbtn:focus-visible { outline: 2px solid var(--vm-accent-bright); outline-offset: 1px; }
.iconbtn svg { width: 18px; height: 18px; }

.disclaimer {
  position: relative; z-index: 1;
  padding: 9px 16px;
  font-size: 10.5px; line-height: 1.55;
  color: var(--vm-faint);
  background: rgba(0,0,0,.16);
  border-bottom: 1px solid var(--vm-border);
}

.progress { position: relative; z-index: 1; padding: 12px 16px 3px; }
.progress-head {
  display: flex; justify-content: space-between;
  font-size: 11px; color: var(--vm-muted); margin-bottom: 7px;
  letter-spacing: .01em;
}
.progress-head span:last-child { color: var(--vm-accent-ink); font-weight: 650; }
.progress-track {
  height: 5px; border-radius: 999px;
  background: rgba(0,0,0,.32); overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0,0,0,.4);
}
.progress-fill {
  height: 100%; border-radius: 999px;
  background: linear-gradient(90deg, var(--vm-accent), var(--vm-accent-bright));
  box-shadow: 0 0 12px rgba(45,212,191,.6);
  transition: width .55s var(--vm-spring);
}

/* ----------------------------------------------------------------- transcript */

.log-wrap { position: relative; z-index: 1; flex: 1; min-height: 0; display: flex; }
.log {
  flex: 1; overflow-y: auto; overscroll-behavior: contain;
  padding: 18px 16px; display: flex; flex-direction: column; gap: 13px;
  scrollbar-width: thin; scrollbar-color: rgba(160,196,255,.2) transparent;
}
.log::-webkit-scrollbar { width: 9px; }
.log::-webkit-scrollbar-thumb {
  background: rgba(160,196,255,.2); border-radius: 9px;
  border: 3px solid transparent; background-clip: content-box;
}
.log::-webkit-scrollbar-thumb:hover {
  background: rgba(160,196,255,.36); background-clip: content-box;
}

/*
 * flex: none on every direct child of .log — and no backticks in this comment,
 * because the whole stylesheet lives inside a JS template literal.
 *
 * The log is a flex column, so its children default to flex-shrink: 1 and get
 * squeezed once the transcript is taller than the panel, instead of scrolling.
 * Messages hide the problem — they simply overflow their box — but .card clips
 * at its overflow: hidden, so a readiness or booking card collapsed to just its
 * heading as soon as the conversation grew.
 */
.row { flex: none; display: flex; align-items: flex-end; gap: 9px; animation: appear .34s var(--vm-spring) both; }
.row.user { justify-content: flex-end; }
@keyframes appear {
  from { opacity: 0; transform: translateY(10px) scale(.98); }
  to   { opacity: 1; transform: none; }
}

.bubble-avatar {
  width: 27px; height: 27px; flex: none;
  display: grid; place-items: center;
  border-radius: 9px; margin-bottom: 2px;
  background: linear-gradient(140deg, rgba(45,212,191,.26), rgba(13,148,136,.12));
  border: 1px solid rgba(45,212,191,.28);
  color: #a7f3e4;
  font-size: 9.5px; font-weight: 700; letter-spacing: .04em;
}
.bubble-avatar.err {
  background: rgba(239,68,68,.18); border-color: rgba(239,68,68,.4);
  color: #fca5a5; font-size: 13px;
}

.msg {
  max-width: 84%; font-size: 14px; line-height: 1.62;
  word-wrap: break-word; overflow-wrap: anywhere;
}
.msg.user {
  /* Dark end of their teal — white text needs 4.5:1 and gets 5.07:1 here. */
  background: linear-gradient(135deg, var(--vm-accent-deep), #0a6f66);
  color: #fff; white-space: pre-wrap;
  padding: 11px 15px;
  border-radius: var(--vm-radius) var(--vm-radius) 5px var(--vm-radius);
  box-shadow:
    0 4px 16px rgba(11,124,114,.34),
    inset 0 1px 0 rgba(255,255,255,.16);
}
.msg.bot {
  background: linear-gradient(160deg, var(--vm-surface), #26355a);
  padding: 12px 15px;
  border-radius: var(--vm-radius) var(--vm-radius) var(--vm-radius) 5px;
  border: 1px solid var(--vm-border);
  box-shadow: 0 3px 14px rgba(0,0,0,.26), inset 0 1px 0 rgba(255,255,255,.06);
}
.msg.err {
  background: linear-gradient(160deg, rgba(239,68,68,.16), rgba(239,68,68,.09));
  border: 1px solid rgba(239,68,68,.34);
  color: #fecaca; padding: 12px 15px; border-radius: var(--vm-radius);
}

/* Rendered markdown inside an assistant bubble. */
.msg.bot p { margin: 0 0 9px; }
.msg.bot p:last-child { margin-bottom: 0; }
.msg.bot h5 {
  margin: 13px 0 6px; font-size: 13px; font-weight: 650;
  color: var(--vm-accent-ink); letter-spacing: .005em;
}
.msg.bot h5:first-child { margin-top: 0; }
.msg.bot ul, .msg.bot ol { margin: 0 0 9px; padding-left: 19px; }
.msg.bot ul:last-child, .msg.bot ol:last-child { margin-bottom: 0; }
.msg.bot li { margin: 4px 0; }
.msg.bot li::marker { color: var(--vm-accent-bright); }
.msg.bot strong { font-weight: 650; color: #fff; }
.msg.bot em { font-style: italic; color: rgba(241,245,249,.86); }
.msg a {
  color: var(--vm-accent-ink); text-decoration: none;
  border-bottom: 1px solid rgba(94,234,212,.42);
  transition: border-color .15s ease, color .15s ease;
}
.msg a:hover { color: #a7f3e4; border-bottom-color: #a7f3e4; }
.inline-code {
  font: 600 12.5px/1.4 var(--vm-mono);
  background: rgba(0,0,0,.36); border: 1px solid var(--vm-border);
  border-radius: 6px; padding: 1.5px 6px; color: var(--vm-accent-ink);
}

/* Covers the wait before the first token lands. */
.msg.bot.typing { display: flex; align-items: center; gap: 5px; padding: 15px 16px; }
.msg.bot.typing i {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--vm-accent-bright); opacity: .45;
  animation: blink 1.4s ease-in-out infinite;
}
.msg.bot.typing i:nth-child(2) { animation-delay: .18s; }
.msg.bot.typing i:nth-child(3) { animation-delay: .36s; }
@keyframes blink {
  0%, 60%, 100% { opacity: .25; transform: translateY(0) scale(.85); }
  30%           { opacity: 1;   transform: translateY(-4px) scale(1); }
}

.activity {
  flex: none;
  align-self: flex-start;
  display: flex; align-items: center; gap: 9px;
  font-size: 11.5px; color: var(--vm-muted);
  padding: 7px 14px; border-radius: 999px;
  background: rgba(0,0,0,.26);
  border: 1px solid var(--vm-border);
  animation: appear .26s var(--vm-spring) both;
}
.spinner {
  width: 12px; height: 12px; flex: none;
  border: 2px solid rgba(255,255,255,.14);
  border-top-color: var(--vm-accent-bright);
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.jump {
  position: absolute; right: 18px; bottom: 14px;
  width: 34px; height: 34px;
  display: grid; place-items: center;
  border: 1px solid var(--vm-border-strong); border-radius: 50%;
  background: var(--vm-raised); color: var(--vm-text);
  cursor: pointer; box-shadow: 0 8px 22px rgba(0,0,0,.5);
  animation: appear .24s var(--vm-spring) both;
  transition: background .18s ease, transform .18s ease;
}
.jump:hover {
  background: var(--vm-accent); border-color: var(--vm-accent-bright);
  transform: translateY(-2px);
}
.jump svg { width: 15px; height: 15px; }

/* --------------------------------------------------------------------- cards */

.card {
  position: relative; overflow: hidden;
  flex: none;
  align-self: stretch;
  background:
    radial-gradient(120% 100% at 100% 0%, rgba(45,212,191,.13), transparent 58%),
    linear-gradient(158deg, var(--vm-surface-2), var(--vm-surface));
  border: 1px solid rgba(45,212,191,.3);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 8px 26px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.09);
  animation: appear .36s var(--vm-spring) both;
}
.card h4 {
  margin: 0 0 7px; font-size: 10.5px; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase; color: var(--vm-accent-ink);
}
.card p { margin: 8px 0 0; font-size: 13px; line-height: 1.58; color: var(--vm-muted); }
.card p.note { font-size: 11.5px; color: var(--vm-faint); }
.card ul {
  margin: 10px 0 0; padding-left: 17px;
  font-size: 13px; line-height: 1.68; color: var(--vm-muted);
}
.card li::marker { color: var(--vm-accent-bright); }

.card.confirmed { border-color: rgba(74,222,128,.42); }
.card.confirmed h4 { color: #86efac; }

.score { display: flex; align-items: baseline; gap: 9px; margin-top: 6px; }
.score b {
  font-size: 38px; font-weight: 700; line-height: 1; letter-spacing: -.03em;
  background: linear-gradient(160deg, #fff, var(--vm-accent-ink));
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.score > span:first-of-type { font-size: 13px; color: var(--vm-faint); }
.band {
  margin-left: auto;
  padding: 5px 11px; border-radius: 999px;
  font-size: 10.5px; font-weight: 650; text-transform: capitalize; letter-spacing: .03em;
  background: var(--vm-accent-soft); color: var(--vm-accent-ink);
  border: 1px solid rgba(45,212,191,.3);
}
.band[data-band="fair"] {
  background: rgba(245,158,11,.16); color: #fcd34d; border-color: rgba(245,158,11,.32);
}
.band[data-band="needs attention"] {
  background: rgba(239,68,68,.16); color: #fca5a5; border-color: rgba(239,68,68,.34);
}

.meter {
  height: 7px; margin-top: 12px; border-radius: 999px;
  background: rgba(0,0,0,.34); overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0,0,0,.45);
}
.meter-fill {
  height: 100%; border-radius: 999px;
  background: linear-gradient(90deg, var(--vm-accent), var(--vm-accent-bright));
  box-shadow: 0 0 12px rgba(45,212,191,.55);
  transition: width .7s var(--vm-spring);
}
.meter-fill[data-band="fair"] {
  background: linear-gradient(90deg, #d97706, #fbbf24);
  box-shadow: 0 0 12px rgba(251,191,36,.5);
}
.meter-fill[data-band="needs attention"] {
  background: linear-gradient(90deg, #dc2626, #f87171);
  box-shadow: 0 0 12px rgba(248,113,113,.5);
}

.program-name { margin-top: 5px; font-size: 20px; font-weight: 650; letter-spacing: -.02em; }
.program-price { margin-top: 4px; font-size: 13.5px; font-weight: 650; color: var(--vm-accent-ink); }

/* Real openings, tappable. */
.slots { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.slot {
  padding: 10px 16px;
  background: linear-gradient(160deg, rgba(45,212,191,.18), rgba(13,148,136,.1));
  border: 1px solid rgba(45,212,191,.42);
  border-radius: 11px;
  color: #fff; font: 650 13px/1 var(--vm-font);
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255,255,255,.1);
  transition: background .2s ease, transform .2s var(--vm-spring), box-shadow .2s ease;
}
.slot:hover:not(:disabled) {
  background: linear-gradient(160deg, var(--vm-accent), var(--vm-accent-deep));
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(13,148,136,.45), inset 0 1px 0 rgba(255,255,255,.2);
}
.slot:disabled { opacity: .38; cursor: default; }
.slot:focus-visible { outline: 2px solid var(--vm-accent-bright); outline-offset: 2px; }

.code {
  display: block; margin-top: 10px; padding: 12px;
  background: rgba(0,0,0,.36); border: 1px dashed rgba(94,234,212,.45);
  border-radius: 11px; font: 700 17px/1 var(--vm-mono);
  letter-spacing: .14em; color: var(--vm-accent-ink); text-align: center;
}
.cta {
  display: block; margin-top: 12px; padding: 12.5px;
  background: linear-gradient(135deg, var(--vm-accent-deep), #0a6f66);
  color: #fff;
  border-radius: 12px; text-align: center;
  font-size: 13.5px; font-weight: 650; text-decoration: none; letter-spacing: -.005em;
  box-shadow: 0 6px 18px rgba(11,124,114,.4), inset 0 1px 0 rgba(255,255,255,.18);
  transition: filter .18s ease, transform .18s var(--vm-spring), box-shadow .18s ease;
}
.cta:hover {
  filter: brightness(1.14); transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(11,124,114,.5), inset 0 1px 0 rgba(255,255,255,.24);
}

/* ------------------------------------------------------------------- prompts */

.chips {
  position: relative; z-index: 1;
  display: flex; flex-wrap: wrap; gap: 8px; padding: 0 16px 13px;
}
.chip {
  text-align: left;
  padding: 9px 14px;
  background: rgba(255,255,255,.05);
  border: 1px solid var(--vm-border);
  border-radius: 999px;
  color: var(--vm-muted); font: 500 12.5px/1.35 var(--vm-font);
  cursor: pointer;
  transition: background .2s ease, border-color .2s ease, color .2s ease,
              transform .2s var(--vm-spring);
}
.chip:hover {
  background: rgba(45,212,191,.16); border-color: rgba(45,212,191,.5);
  color: #fff; transform: translateY(-2px);
}
.chip:focus-visible { outline: 2px solid var(--vm-accent-bright); outline-offset: 1px; }

/* ------------------------------------------------------------------ composer */

/* The textarea and the send button share one bordered capsule rather than
 * sitting side by side as two separate controls. Two bordered boxes read as two
 * things to deal with; one reads as a place to type, which is what it is. The
 * whole capsule lights on :focus-within, so the focus ring lands on the object
 * the user is actually working in. */
.composer {
  position: relative; z-index: 2;
  padding: 12px 14px 10px;
  border-top: 1px solid var(--vm-border);
  background: linear-gradient(180deg, rgba(26,37,64,.72), rgba(20,29,50,.92));
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
}
.field {
  display: flex; align-items: flex-end; gap: 6px;
  padding: 5px 5px 5px 6px;
  background: rgba(0,0,0,.3);
  border: 1px solid var(--vm-border); border-radius: 16px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,.32);
  transition: border-color .2s ease, box-shadow .2s ease, background .2s ease;
}
.field:focus-within {
  border-color: rgba(45,212,191,.65);
  background: rgba(0,0,0,.22);
  box-shadow: inset 0 1px 3px rgba(0,0,0,.3), 0 0 0 3px rgba(45,212,191,.16);
}
.field textarea {
  flex: 1; resize: none;
  min-height: 34px; max-height: 124px;
  padding: 8px 4px 8px 8px;
  background: transparent; border: 0; box-shadow: none;
  color: var(--vm-text); font: 400 14px/1.35 var(--vm-font);
  outline: none;
}
.field textarea::placeholder { color: rgba(241,245,249,.38); }

/* Ghosted until there is something to send, then it fills with the brand teal.
 * The button doubles as the affordance that says the field is ready. */
.send {
  width: 34px; height: 34px; flex: none;
  display: grid; place-items: center;
  border: 1px solid transparent; border-radius: 12px;
  background: transparent; color: rgba(241,245,249,.42);
  cursor: pointer;
  transition: background .18s ease, color .18s ease, border-color .18s ease,
              box-shadow .18s ease, transform .18s var(--vm-spring);
}
.send:not(:disabled) {
  background: linear-gradient(135deg, var(--vm-accent-deep), #0a6f66);
  color: #fff;
  box-shadow: 0 3px 10px rgba(11,124,114,.36), inset 0 1px 0 rgba(255,255,255,.18);
}
.send:hover:not(:disabled) { filter: brightness(1.16); transform: translateY(-1px); }
.send:disabled { cursor: default; }
.send:focus-visible { outline: 2px solid var(--vm-accent-bright); outline-offset: 2px; }
.send svg { width: 16px; height: 16px; }
.send.stop {
  background: var(--vm-raised); border-color: var(--vm-border-strong);
  color: var(--vm-text); box-shadow: none;
}
.send.stop:hover { background: rgba(239,68,68,.24); border-color: rgba(239,68,68,.45); }

.footer {
  position: relative; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  gap: 7px; flex-wrap: wrap;
  padding: 7px 14px 11px;
  text-align: center; font-size: 10.5px; color: rgba(241,245,249,.36);
  background: rgba(20,29,50,.92);
  letter-spacing: .01em;
}
/* Vendor attribution. Muted enough to stay out of the practice's way, legible
 * enough to be worth having — this is our name on their site. */
.brand { color: rgba(241,245,249,.36); text-decoration: none; white-space: nowrap; }
.brand b { color: var(--vm-accent-ink); font-weight: 650; }
.brand:hover b { color: var(--vm-accent-bright); text-decoration: underline; }
.brand:focus-visible { outline: 2px solid var(--vm-accent-bright); outline-offset: 2px; border-radius: 4px; }

/* -------------------------------------------------------------------- mobile */

/*
 * iOS Safari zooms the whole page when a focused field's text is under 16px, and
 * it does not zoom back out on blur. At 14px, a visitor tapping our composer left
 * the practice's ENTIRE site stuck at ~1.15x until they pinched out manually —
 * our widget breaking their pages. 16px on touch input is the fix; the 1px of
 * extra line height is invisible next to that.
 *
 * Keyed on pointer:coarse rather than a width, because an iPad is 768-1024px
 * wide and zooms exactly the same way.
 */
@media (pointer: coarse) {
  .field textarea { font-size: 16px; }
  /* Touch targets. 34px is comfortable with a mouse and small for a thumb;
     44px is the platform minimum on both iOS and Android. */
  .send { width: 42px; height: 42px; }
  .iconbtn { padding: 11px; }
  .iconbtn:hover { transform: none; }   /* no hover state on touch */
}

@media (max-width: 520px) {
  /*
   * left + right together make .root full width, and .launcher is display:flex —
   * which is block-level, so it stretched edge to edge and the corner pill became
   * a full-width bar with its dot marooned on the far right. The panel wants the
   * full width; the launcher does not. Aligning the flex container's children to
   * the end gives each what it needs.
   */
  .root {
    right: 12px; left: 12px;
    bottom: max(12px, env(safe-area-inset-bottom));
    display: flex; flex-direction: column; align-items: flex-end;
  }
  .launcher { padding: 14px 22px 14px 18px; font-size: 14.5px; }
  /* A practice can set any data-label; without this a long one wraps into a
     two-line pill or pushes past the screen edge. */
  .launcher-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 58vw; }

  .panel {
    width: 100%;
    /* dvh tracks the shrinking viewport when the keyboard opens, which vh does
       not. The subtraction is the 12px inset plus the same again at the top. */
    height: min(650px, calc(100dvh - 24px - env(safe-area-inset-bottom)));
    border-radius: 20px;
  }
  .msg { max-width: 88%; }
  .header { padding: 13px 14px; }
  .composer { padding: 10px 12px 8px; }
}

/*
 * Landscape phone: ~360px of panel height to hold a header, a disclaimer, the
 * transcript, the composer and the footer. Measured before this rule existed,
 * the transcript got 36px — about one line — because .chips is a SIBLING of the
 * transcript, not part of it, and four wrapped suggestion rows took ~180px.
 *
 * So the suggestions become one horizontally scrolling row. They still earn
 * their place (they are what a visitor taps first), they just stop paying for it
 * out of the conversation's height.
 *
 * The disclaimer stays. An earlier version of this rule hid it to buy 34px, which
 * is the wrong 34px: it carries "not medical advice" and "call 911", and a legal
 * notice that disappears in landscape is worse than a cramped one.
 */
@media (max-height: 460px) and (orientation: landscape) {
  .root { bottom: 8px; }
  .panel { height: calc(100dvh - 16px); border-radius: 16px; }
  .header { padding: 8px 14px; }
  .avatar { width: 30px; height: 30px; border-radius: 9px; font-size: 11.5px; }
  .title span { display: none; }
  .disclaimer { padding: 6px 14px; font-size: 10px; line-height: 1.45; }
  .log { padding: 12px 14px; gap: 10px; }
  .chips {
    flex-wrap: nowrap; overflow-x: auto; padding: 0 14px 9px;
    scrollbar-width: none;
  }
  .chips::-webkit-scrollbar { display: none; }
  .chip { flex: none; }
  .composer { padding: 8px 12px 6px; }
  .footer { padding: 4px 14px 6px; }
}

@media (prefers-reduced-motion: reduce) {
  .spinner, .launcher-dot, .msg.bot.typing i, .launcher::after { animation: none; }
  .panel, .row, .card, .activity, .jump { animation: none; }
  .launcher, .progress-fill, .meter-fill, .cta, .send, .chip, .slot, .iconbtn {
    transition: none;
  }
  .iconbtn:hover { transform: none; }
}
`;
